import React, { useState } from "react";
import { FiMail } from "react-icons/fi";
import { toast } from "react-toastify";
import styles from "./styles.module.css";
import Modal from "../Modal";
import ManageEmails from "../../modals-content/ManageEmails";
import Checkbox from "../Checkbox";
import ConsentManager from "../../database/ConsentManager";
import { RegisteredUser } from "../../types/generic";
import { Button, PageHeading, formatDate } from "../UI";
type Props = {
  user: RegisteredUser;
  setUser: (user: RegisteredUser) => void;
  refreshProfile: () => void;
};
export default function UserDetails({ user, refreshProfile }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const close = () => setIsModalOpen(false);
  const updateConsent = async (
    kind: "faults" | "analytics",
    value: boolean,
  ) => {
    if (pending) return;
    setPending(kind);
    try {
      if (kind === "faults")
        await ConsentManager.setAutomatedFaultReportingConsent(
          user.email,
          value,
        );
      else await ConsentManager.setAnalyticsConsent(user.email, value);
      refreshProfile();
    } catch (error) {
      toast.error("Could not update this setting. Please try again.");
      console.error(error);
    } finally {
      setPending(null);
    }
  };
  const registrationDate =
    user.date || ("createdAt" in user ? (user.createdAt as string) : undefined);
  const details = [
    ["Primary email", user.email],
    ["Alternative email", user.alternativeEmails?.join(", ") || "None"],
    ["School / establishment", user.establishment || "—"],
    ["Registered", formatDate(registrationDate)],
  ];
  return (
    <>
      <PageHeading title="Account" />
      <section
        className={`${styles.panel} ${styles.profile}`}
        aria-labelledby="account-details-heading"
      >
        <div className={styles.profileHeading}>
          <h2 id="account-details-heading">Account details</h2>
          <Button onClick={() => setIsModalOpen(true)}>
            <FiMail aria-hidden="true" />
            Manage emails
          </Button>
        </div>
        <dl className={styles.details}>
          {details.map(([label, value]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section className={styles.panel} aria-labelledby="reporting-heading">
        <h2 id="reporting-heading">Reporting &amp; data</h2>
        <div className={styles.preference}>
          <div>
            <h3>Automatic fault reporting</h3>
            <p>Report detected robot faults automatically.</p>
          </div>
          <Checkbox
            label="Automatic fault reporting"
            checked={
              user.automatedFaultReportingConsent === undefined
                ? true
                : user.automatedFaultReportingConsent
            }
            disabled={pending !== null}
            onChange={(value) => updateConsent("faults", value)}
          />
        </div>
        <div className={styles.preference}>
          <div>
            <h3>Usage analytics</h3>
            <p>Share robot usage data with Robotical.</p>
          </div>
          <Checkbox
            label="Usage analytics"
            checked={
              user.analyticsConsent === undefined ? true : user.analyticsConsent
            }
            disabled={pending !== null}
            onChange={(value) => updateConsent("analytics", value)}
          />
        </div>
        {pending && (
          <span className="sr-only" role="status">
            Saving preference…
          </span>
        )}
      </section>
      <Modal show={isModalOpen} title="Manage emails" onClose={close}>
        <ManageEmails
          primaryEmail={user.email}
          alternativeEmails={user.alternativeEmails || []}
          refreshProfile={refreshProfile}
          onClose={close}
        />
      </Modal>
    </>
  );
}
